import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { getOrderShipmentsApi, getOrdersApi } from 'api/order';
import useInfoDialog from 'common/hooks/useInfoDialog';
import { first, isEmpty, trim } from 'lodash';
import ShipmentViewDialog from 'modules/WorkQueue/components/ShipmentViewDialog';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch } from 'react-redux';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { refreshAfterMutation } from 'store/thunks/shipmentModuleThunks';

const SearchShipmentDialog = () => {
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [searching, setSearching] = useState(false);
  const lastKeyStrokeTime = useRef(Date.now());
  const isScannerDetected = useRef(true);
  const autoSearchedValue = useRef('');

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSearch = useCallback(
    (isAuto = false) => {
      const term = trim(inputValue);

      if (!term || searching) return;

      if (isAuto && term === autoSearchedValue.current) return;

      setSearching(true);

      if (isAuto) {
        autoSearchedValue.current = term;
      }

      getOrderShipmentsApi({
        limit: 1,
        offset: 0,
        filter: {
          flatSearch: term,
        },
        sortBy: {
          sortKey: 'orderDate',
          sortOrder: 'DESC',
        },
      })
        .then(({ result }) => {
          if (isEmpty(result)) {
            // Check if order exists in a different status before showing "not found"
            return getOrdersApi({
              page: 1,
              filter: { orderId: term },
            })
              .then(({ orders }) => {
                if (isEmpty(orders)) {
                  showInfoDialog({
                    title: 'Order Not Found',
                    message: `No order with ${term} order id was found, please try again.`,
                  });
                } else {
                  const order = first(orders);
                  showInfoDialog({
                    title: 'Order Found',
                    message: `Order ${term} was found with status: ${order.status}. It may not be available for shipment in this view.`,
                  });
                }
              })
              .catch((error) => {
                dispatch(handleError({ error }));
              });
          }

          const found = first(result);
          window.dispatchEvent(new CustomEvent('closeShipmentViewDialog'));
          setSelectedShipment(found);
          handleClose();
          return undefined;
        })
        .catch((error) => dispatch(handleError({ error })))
        .finally(() => setSearching(false));
    },
    [inputValue, searching, dispatch, showInfoDialog]
  );

  const onChange = (event) => {
    const newValue = event.target.value;
    const now = Date.now();
    const addedChars = newValue.length - inputValue.length;

    if (addedChars > 1 || newValue.length <= 1) {
      isScannerDetected.current = true;
    } else {
      const diff = now - lastKeyStrokeTime.current;
      if (diff > 50) {
        isScannerDetected.current = false;
      }
    }

    if (newValue !== autoSearchedValue.current) {
      autoSearchedValue.current = '';
    }

    lastKeyStrokeTime.current = now;
    setInputValue(newValue);
  };

  const closeShipment = () => {
    setSelectedShipment(null);
    handleOpen();
  };

  useEffect(() => {
    const term = trim(inputValue);

    if (isScannerDetected.current && term.length >= 5 && term !== autoSearchedValue.current) {
      const delayDebounceFn = setTimeout(() => {
        handleSearch(true);
      }, 150);

      return () => clearTimeout(delayDebounceFn);
    }

    return undefined;
  }, [inputValue, handleSearch]);

  useEffect(() => {
    if (!open) {
      setInputValue('');
      autoSearchedValue.current = '';
      isScannerDetected.current = true;
    }
  }, [open]);

  useHotkeys('ctrl + shift + s', handleOpen);

  return (
    <>
      <InfoDialog />

      <ShipmentViewDialog
        key={selectedShipment?.spOrderId}
        open={!!selectedShipment}
        onClose={closeShipment}
        shipment={selectedShipment}
        onAfterMutation={() => dispatch(refreshAfterMutation())}
      />

      <Dialog open={open} fullWidth maxWidth='sm'>
        <DialogTitle>Search Shipment By Order Id</DialogTitle>

        <DialogContent>
          <TextField
            sx={{ mt: 3 }}
            label='Order Id'
            autoFocus
            onChange={onChange}
            value={inputValue}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={searching}
            helperText='Must be at least 5 characters'
          />
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={handleClose} sx={{ width: 75 }} disabled={searching}>
            Close
          </Button>

          <LoadingButton
            loading={searching}
            variant='contained'
            onClick={() => handleSearch(false)} // Manual click ignores the auto-lock
            sx={{ width: 75 }}
            disabled={searching || trim(inputValue).length < 5}
          >
            Search
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SearchShipmentDialog;
